import {
  castToDefaultValue,
  getByPath,
  hasValue,
  stringToBoolean,
} from '@r-and-a-shared-ui/utils';
import { FormStepProps } from '../FormStep/FormStep';
import { FormFieldProps } from '../fields';
import {
  CHECKBOXES,
  DATE,
  NUMBERED_CHECKBOXES,
  NUMBERED_CHECKBOXES_WITH_OTHER,
  PP_DROPDOWN,
} from '../constants';
import { isRelationFulfilled } from 'libs/common-kontent-client/src/utils/helpers/relationComparisonHelpers';
import { getFieldsByStepIndex, SkippingStepConfig } from './config-helper';
import { STEP } from './../constants/configConstants';

export const setSpecialFieldsValues = (
  defaultValues: any,
  separator: string,
  steps: FormStepProps[] | undefined,
  queryResultDefaultValues?: any,
) => {
  if (!defaultValues || !steps) {
    return;
  }

  steps.forEach(step =>
    step.fieldsData?.forEach(fieldData => {
      if (fieldData.fieldType === DATE) {
        setDateValues(defaultValues, separator, fieldData);
      }
      if (
        fieldData.fieldType === CHECKBOXES ||
        fieldData.fieldType === NUMBERED_CHECKBOXES ||
        fieldData.fieldType === NUMBERED_CHECKBOXES_WITH_OTHER
      ) {
        setCheckboxesValue(defaultValues, separator, fieldData);
      }
      if (fieldData.fieldType === PP_DROPDOWN) {
        setPPDropdownValue(
          defaultValues,
          separator,
          fieldData,
          queryResultDefaultValues,
        );
      }
    }),
  );
};

const setDateValues = (
  defaultValues: any,
  separator: string,
  fieldData: FormFieldProps,
) => {
  if (defaultValues[fieldData.name]) {
    const dateValue = new Date(defaultValues[fieldData.name]);
    fieldData.subfields?.forEach(subfield => {
      const subFieldName = subfield?.name;
      if (subFieldName) {
        const indicator = subFieldName.split(separator).slice(-1);
        if (indicator.includes('date')) {
          defaultValues[subFieldName] = dateValue.getDate().toString();
        } else if (indicator.includes('month')) {
          defaultValues[subFieldName] = (dateValue.getMonth() + 1).toString();
        } else if (indicator.includes('year')) {
          defaultValues[subFieldName] = dateValue.getFullYear().toString();
        }
      }
    });
  } else {
    fieldData.subfields?.forEach(subfield => {
      const subFieldName = subfield?.name;
      if (subFieldName) defaultValues[subFieldName] = '';
    });
  }
};

const setCheckboxesValue = (
  defaultValues: any,
  separator: string,
  fieldData: FormFieldProps,
) => {
  //Used to keep the previous value when field is true or false
  const previousFieldValue = defaultValues[fieldData.name];

  const isTrue = stringToBoolean(previousFieldValue);

  if (isTrue) defaultValues[fieldData.name] = [];

  let index = 0;
  const defValuesKeys = Object.keys(defaultValues);

  fieldData.options?.forEach(option => {
    const currentKey = getCurrentKey(separator, fieldData, index);
    if (
      defValuesKeys.includes(currentKey) &&
      (isTrue || defaultValues[currentKey] === option.value)
    ) {
      defaultValues[fieldData.name].push(option.value);
      index++;
    }
  });
};

const setPPDropdownValue = (
  defaultValues: any,
  separator: string,
  fieldData: FormFieldProps,
  queryResultDefaultValues?: any,
) => {
  defaultValues[fieldData.name] = getByPath(
    queryResultDefaultValues,
    fieldData.name,
    separator,
  );
};

const getCurrentKey = (
  separator: string,
  fieldData: FormFieldProps,
  index: number,
): string => {
  if (fieldData?.options?.length === 1) {
    return `${fieldData.name}`;
  }
  return `${fieldData.name}${separator}${index}`;
};

export const shouldSkipStep = (
  formStepProps: FormStepProps[] | undefined,
  stepNumber: number,
  fieldValue: any,
) => {
  if (
    !hasValue(formStepProps) ||
    typeof formStepProps === 'undefined' ||
    stepNumber >= formStepProps.length
  ) {
    return false;
  }
  const { displayHideStepBasedOnFieldValue } = formStepProps[stepNumber];

  if (
    displayHideStepBasedOnFieldValue &&
    (!hasValue(fieldValue) ||
      !isRelationFulfilled(fieldValue, displayHideStepBasedOnFieldValue?.item))
  ) {
    return true;
  }
  return false;
};

export const resetSkippedStepValues = (
  skippingStepConfig: SkippingStepConfig | undefined,
  stepNumber: number,
) => {
  const { values, steps, setValue } = skippingStepConfig || {};

  if (!values || !steps || !setValue) {
    return;
  }

  const fieldNames = getFieldsByStepIndex(stepNumber, steps);
  fieldNames?.forEach(fieldName => {
    const value = castToDefaultValue(values[fieldName]);
    setValue(fieldName, value);
  });
};

export const getVisibleStep = (
  stepNumber: number,
  direction: 1 | -1,
  skippingStepConfig?: SkippingStepConfig,
): number => {
  const { values, steps } = skippingStepConfig || {};
  if (!values || !steps || steps.length === 0) {
    return stepNumber;
  }

  const canSkip = (step: number): boolean => {
    const key = steps[step]?.displayHideStepBasedOnFieldValue?.key;
    if (!key) {
      return false;
    }
    return shouldSkipStep(steps, step, values[key]);
  };

  let nextStep = stepNumber;

  while (canSkip(nextStep)) {
    resetSkippedStepValues(skippingStepConfig, nextStep);
    nextStep += direction;

    if (nextStep < 0 || nextStep > steps.length - 1) {
      break;
    }
  }

  return nextStep;
};

export const getStepIndexKey = (formCodename?: string): string => {
  return formCodename ? `${formCodename}_${STEP}` : `${STEP}`;
};
